import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Button } from "iuvity-ui-components/dist/button";
import { Column } from "primereact/column";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { FormSkeleton, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { TranslationsImporter } from "../../components/translation/TranslationsImporter";
import { FilterDialog } from "../../components/consoletranslations/FilterDialog";
import { ConsoleTranslationCreator } from "../../components/consoletranslations/ConsoleTranslationCreator";
import { useConsoleTranslation } from "../../hooks/useConsoleTranslations";
import { ConsoleTranslation } from "../../interfaces";
import { LANGUAGE_OPTIONS, REVALIDATE_FORM_PROPS } from "../../constants";
import { AppContext } from "../../context/index";
import { InputText } from "iuvity-ui-components";
import { MultiSelect } from 'primereact/multiselect';
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

const formBlankValues = {
  functionality: [],
  key: "",
  type: [],
  translationvalue: "",
  translationlanguage: [],
};

export const AdminConsoleTranslationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { user, language, pageFilter, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });

  const { control, setValue, getValues } = form;

  const { data, translationAction, reloadFn } = useConsoleTranslation("DATA");
  const [filteredData, setFilteredData] = useState<any[] | null>(null);
  const [filterModalVisible, setFilterModalVisible] = useState<boolean>(false);
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [modalProps, setModalProps] = useState<{
    visible: boolean;
    type: "CREATE" | "UPDATE" | null;
  }>({
    visible: false,
    type: null,
  });
  const [auxModal, auxModalController] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const columns = useMemo(() => {
    return [
      {
        field: "functionality",
        header: t("ConsoleTranslations.functionalityLabel"),
      },
      {
        field: "key",
        header: t("ConsoleTranslations.keyLabel"),
      },
      {
        field: "type",
        header: t("ConsoleTranslations.typeLabel"),
      },
      {
        field: "translationvalue",
        header: t("ConsoleTranslations.translationvalueLabel"),
      },
      {
        field: "translationlanguage",
        header: t("ConsoleTranslations.translationlanguageLabel"),
      },
      {
        field: "actions",
        header: t("ConsoleTranslations.actionsLabel"),
      },
    ];
  }, [t]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    functionality: { value: pageFilter?.functionality, matchMode: FilterMatchMode.IN },
    key: { value: pageFilter?.key, matchMode: FilterMatchMode.IN },
    type: { value: pageFilter?.type, matchMode: FilterMatchMode.IN },
    translationvalue: { value: pageFilter?.translationvalue, matchMode: FilterMatchMode.CONTAINS },
    translationlanguage: { value: pageFilter?.translationlanguage, matchMode: FilterMatchMode.IN },
  };

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const formOptions = useMemo(() => {
    if (data) {
      return {
        functionality: [
          ...new Set(data.flatMap((item: any) => item?.functionality)),
        ]
          .map((item) => ({
            value: item,
            label: `${item}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        type: [...new Set(data.flatMap((item: any) => item.type))]
          .map((item) => ({
            value: item,
            label: t(`Translations.${`${item}`.toLowerCase()}OptionLabel`),
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        translationlanguage: LANGUAGE_OPTIONS(t),
      };
    } else {
      return;
    }
  }, [t, data]);

  const onEditAction = (label?: ConsoleTranslation) => {
    setAppState((prev) => ({
      ...prev,
      selectedConsoleLabel: label,
    }));
    navigate("/consoletranslations/edit");
  };

  const actionDeleteFn = async (label: ConsoleTranslation) => {
    await translationAction("DELETE", label.translationid);
    reloadFn();
  };

  const onDownloadTranslations = async () => {
    const matchKeys = {
      functionality: "consolefunctionality",
      translationlanguage: "languageisocode",
    };
    const itemsToFilter = Object.fromEntries(
      Object.entries(form.getValues())
        .filter(([_, value]) => value.length > 0)
        .map(([key, value]) => [(matchKeys as any)[key] || key, value])
    );
    await translationAction("DOWNLOAD", {
      filename: `${t("ConsoleTranslations.consoletranslationsTitle").replaceAll(
        " ",
        ""
      )}_${
        new Date().toISOString().substring(0, 10).replace(/-|:/g, "") +
        "-" +
        new Date().toLocaleTimeString("es-ES").replace(/-|:/g, "")
      }`,
      filtersInfo: Object.keys(itemsToFilter).length > 0 && itemsToFilter,
    });
  };

  const handleModalProps = async (
    type: "DOWNLOAD" | "DELETE" | "IMPORT",
    label?: ConsoleTranslation
  ) => {
    switch (type) {
      case "DOWNLOAD":
        return auxModalController((prev) => ({
          ...prev,
          visible: true,
          title: t("ConsoleTranslations.downloadModalTitle"),
          content: t("ConsoleTranslations.downloadModalContent"),
          onConfirm: () => onDownloadTranslations(),
        }));
      case "DELETE":
        return auxModalController((prev) => ({
          ...prev,
          visible: true,
          title: t("ConsoleTranslations.deleteModalTitle"),
          content: `${t("ConsoleTranslations.deleteModalContent1")} "${
            label?.key
          } - ${
            LANGUAGE_OPTIONS(t).find(
              (lan) => lan.value === label?.translationlanguage
            )?.label
          }" ${t("ConsoleTranslations.deleteModalContent2")}`,
          onConfirm: () => actionDeleteFn(label!),
        }));

      default:
        return;
    }
  };

  const onClearFilters = () => {
    setFilteredData(data);
    form.reset(formBlankValues);
  };

  const onClearFiltersColumn = () => {
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`ConsoleTranslations.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`ConsoleTranslations.${options.field}PlaceholderLabel`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };
  const multiselectFilterTemplate = (options: any) => {
    const arrayOriginal = data
      ?.map((label: any) => ({
      ...label,
      type: t(`Translations.${label?.type.toLowerCase()}OptionLabel`),
      translationlanguage: t(`Languages.${label?.translationlanguage}`),
      }))
      ?.map((item: any) => (
        {
          label: Array.isArray(item[`${options.field}`]) ? item[`${options.field}`][0] : item[`${options.field}`], 
          value: Array.isArray(item[`${options.field}`]) ? item[`${options.field}`][0] : item[`${options.field}`]
        }))
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label));

    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      <Controller
          name={options.field}
          control={control}
          // rules={PermissionsFormSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`ConsoleTranslations.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`ConsoleTranslations.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("ConsoleTranslations.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.applyFilterLabel")}
        className="p-button"
        onClick={()=>{
          options.filterApplyCallback();
          setAppState((prev: any)=>({
            ...prev,
            // pageFilter: getValues(),
          }));
        }}
        disabled={
          !(
            getValues('functionality').length > 0 ||
            getValues('key').length > 0 ||
            getValues('type').length > 0 ||
            getValues('translationvalue').length > 0 ||
            getValues('translationlanguage').length > 0
          )
        }
      />
    )
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFiltersColumn}
      />
    )
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`ConsoleTranslations.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("ConsoleTranslations.consoletranslationsTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("ConsoleTranslations.adminConsoleMenuLabel"),
              disabled: true
            },
            {
              label: t("ConsoleTranslations.consoletranslationsTitle"),
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("ConsoleTranslations.translationsDescription")}</span>
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        className="multiactions-c"
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("ConsoleTranslations.newText"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => navigate(`${location.pathname}/new`),
            disabled: !data,
          },
          {
            id: "222",
            icon: "iu iu-icon-download",
            label: t("ConsoleTranslations.downloadBtnLabel"),
            onClick: () => handleModalProps("DOWNLOAD"),
            style: { width: "10rem !important" },
            disabled: !!(filteredData && filteredData?.length === 0) || !data,
          },
          {
            id: "333",
            icon: "iu iu-icon-upload",
            label: t("ConsoleTranslations.importTranslationsTitle"),
            onClick: () => setImportModalVisible(true),
            style: { width: "10rem !important" },
            disabled: !data,
          },
        ])}
      />

      <BoxFull className="dt-c my-4">
        {filteredData ? (
          <DataTable
            header={
              <TableHeader
                title={t("ConsoleTranslations.consoletranslationsTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFiltersColumn}
                globalFilterValue={globalFilterValue}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            editMode="row"
            dataKey="id"
            sortField="functionality"
            sortOrder={1}
            filters={filters}
            translations="en"
            value={filteredData.map((label: any) => ({
              ...label,
              type:
                label?.type !== "."
                  ? t(`Translations.${label?.type.toLowerCase()}OptionLabel`)
                  : ".",
              functionality: label?.functionality.join(", "),
              translationlanguage: t(`Languages.${label?.translationlanguage}`),
              actions: (
                <Actions
                  enableDelete={true}
                  onEdit={() => onEditAction(label)}
                  onDelete={() => handleModalProps("DELETE", label)}
                  visibleObject={{...permissiondInfoByRole}}
                />
              ),
            }))}
            scrollable
            resizableColumns
            globalFilterFields={['functionality', 'key', 'type', 'translationvalue', 'translationlanguage']}
            filterDisplay="menu"
            stateStorage="session"
            stateKey="filters-table"
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                  ? {
                      justifyContent: "center",
                      display: (!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined,
                    }
                  : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : (col.field === "translationvalue") ? "250px" : undefined,
                  whiteSpace: "normal",
                  minWidth: (col.field === "actions") ? "8.3rem" : "14rem",
                  display: (col.field === "actions")
                    ? ((!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined)
                    : undefined,
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                filterField={col.field}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: '23rem'}}
                filter={col.field === "actions" ? false : true}
                filterElement={
                  ((col.field === "translationvalue") && filterTemplate) ||
                  ((col.field !== "translationvalue") && multiselectFilterTemplate)
                }
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      <FilterDialog
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        data={data}
        setData={setFilteredData}
        onClearFilters={onClearFilters}
        form={form}
        formOptions={formOptions}
      />

      {modalProps.visible && formOptions && (
        <ConsoleTranslationCreator
          visible={modalProps.visible}
          setModalProps={setModalProps}
          update={modalProps.type === "UPDATE"}
          actionFn={translationAction}
          reloadFn={reloadFn}
          auxModalController={auxModalController}
        />
      )}

      {importModalVisible && (
        <TranslationsImporter
          allowedIDs={data.map((label: any) => `${label.id}`)}
          columns={[
            {
              field: "id",
              header: "id",
            },
            ...columns.filter((col: any) => col?.field !== "actions"),
            {
              field: "description",
              header: t("ConsoleTranslations.descriptionLabel"),
            },
          ]}
          importFn={translationAction}
          reloadFn={reloadFn}
          setVisible={setImportModalVisible}
          auxModalController={auxModalController}
        />
      )}

      {data && importModalVisible && (
        <TranslationsImporter
          allowedIDs={
            [...new Set(data.map((item: any) => `${item?.id}`))] as string[]
          }
          columns={[
            {
              field: "id",
              header: "id",
            },
            ...columns.filter(
              (col: any) =>
                ![
                  "translationvalue",
                  "translationlanguage",
                  "actions",
                ].includes(col.field)
            ),
            {
              field: "esvalue",
              header: t("Translations.esvalueLabel"),
            },
            {
              field: "envalue",
              header: t("Translations.envalueLabel"),
            },
          ]}
          importFn={translationAction}
          reloadFn={reloadFn}
          setVisible={setImportModalVisible}
          auxModalController={auxModalController}
          headers={["id", "functionality", "key", "type", "es", "en"]}
        />
      )}

      <CustomDialog
        title={auxModal.title}
        visible={auxModal.visible}
        content={auxModal.content}
        handleModalState={auxModalController}
        onConfirm={auxModal.onConfirm}
      />
    </>
  );
};
